import { logger } from '@jobilla/instrumentation'
import { useCandidacyCvStore } from '@jobilla/nexus-sdk/lib/hooks/useCandidacyCv'
import { candidateFileExtensions } from '@jobilla/nexus-sdk/lib/types/CandidateFileExtensions'
import { maxMediaFileSizeKb } from '@jobilla/nexus-sdk/lib/types/MaxMediaFileSizeKb'
import { useNavigate } from '@tanstack/react-router'
import { type ChangeEvent, type DragEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const ACCEPTED_EXTENSIONS: readonly string[] = candidateFileExtensions

const ACCEPTED_TYPES_LABEL = candidateFileExtensions.map(extension => extension.toUpperCase()).join(', ')

const MAX_SIZE_IN_BYTES = maxMediaFileSizeKb * 1024

const MAX_SIZE_IN_MB = maxMediaFileSizeKb / 1024

const SUCCESS_DURATION_IN_MS = 1200

type UploadState =
	| { status: 'idle' }
	| { status: 'uploading'; fileName: string; progress: number | null }
	| { status: 'done' }
	| { status: 'error'; reason: 'type' | 'size' | 'throttled' | 'failed' }

export function CvUpload(
	{ organisationId, candidacyId, onUploaded }: {
		organisationId: string
		candidacyId: string
		onUploaded: () => void
	},
) {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const [uploadState, setUploadState] = useState<UploadState>({ status: 'idle' })
	const [isDragging, setIsDragging] = useState(false)
	const successTimeoutRef = useRef<number | null>(null)
	const isBusy = uploadState.status === 'uploading' || uploadState.status === 'done'

	const storeCv = useCandidacyCvStore(undefined, {
		onUploadProgress: progressEvent => {
			if (progressEvent.total === undefined || progressEvent.total === 0) {
				return
			}
			const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
			setUploadState(current => current.status === 'uploading' ? { ...current, progress } : current)
		},
	})

	useEffect(() => () => {
		if (successTimeoutRef.current !== null) {
			window.clearTimeout(successTimeoutRef.current)
		}
	}, [])

	const uploadFile = (file: File) => {
		const extensionIndex = file.name.lastIndexOf('.')
		const extension = extensionIndex === -1 ? '' : file.name.slice(extensionIndex + 1).toLowerCase()

		if (!ACCEPTED_EXTENSIONS.includes(extension)) {
			setUploadState({ status: 'error', reason: 'type' })
			return
		}

		if (file.size > MAX_SIZE_IN_BYTES) {
			setUploadState({ status: 'error', reason: 'size' })
			return
		}

		setUploadState({ status: 'uploading', fileName: file.name, progress: null })

		storeCv.mutate({ organisation: organisationId, candidacy: candidacyId, data: { file } }, {
			onSuccess: () => {
				logger.track('Candidate Center: Uploaded CV', { data: { extension, candidate_id: candidacyId } })
				setUploadState({ status: 'done' })
				successTimeoutRef.current = window.setTimeout(onUploaded, SUCCESS_DURATION_IN_MS)
			},
			onError: error => {
				const status = error.response?.status ?? null
				logger.track('Candidate Center: CV upload failed', { data: { status, candidate_id: candidacyId } })

				if (status === 401) {
					void navigate({ to: '/', replace: true })
					return
				}

				setUploadState({ status: 'error', reason: status === 429 ? 'throttled' : 'failed' })
			},
		})
	}

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		event.target.value = ''
		if (file !== undefined) {
			uploadFile(file)
		}
	}

	const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
		event.preventDefault()
		if (!isBusy) {
			setIsDragging(true)
		}
	}

	const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
		event.preventDefault()
		setIsDragging(false)
		const file = event.dataTransfer.files[0]
		if (file !== undefined && !isBusy) {
			uploadFile(file)
		}
	}

	const zoneClassName = uploadState.status === 'done'
		? 'cursor-default border-solid border-green-500 bg-green-50'
		: isDragging
		? 'cursor-pointer border-dashed border-brand-500 bg-brand-50'
		: isBusy
		? 'cursor-default border-dashed border-grey-300'
		: 'cursor-pointer border-dashed border-grey-300 hover:border-grey-400 hover:bg-grey-50'

	return (
		<div>
			<label
				onDragOver={handleDragOver}
				onDragLeave={() => setIsDragging(false)}
				onDrop={handleDrop}
				className={`flex min-h-26 flex-col items-center justify-center gap-1 rounded-lg border-2 px-6 py-6 text-center transition-colors focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-600 ${zoneClassName}`}
			>
				<input
					type="file"
					accept={ACCEPTED_EXTENSIONS.map(extension => `.${extension}`).join(',')}
					disabled={isBusy}
					onChange={handleChange}
					className="sr-only"
				/>
				<UploadZoneContent uploadState={uploadState} />
			</label>
			{uploadState.status === 'error'
				? (
					<p role="alert" className="mt-2 text-sm text-red-600">
						{uploadState.reason === 'type'
							? t('profile.cv.upload.error_type', { types: ACCEPTED_TYPES_LABEL })
							: uploadState.reason === 'size'
							? t('profile.cv.upload.error_size', { size: MAX_SIZE_IN_MB })
							: uploadState.reason === 'throttled'
							? t('login.request_error.throttled')
							: t('login.request_error.generic')}
					</p>
				)
				: null}
		</div>
	)
}

function UploadZoneContent({ uploadState }: { uploadState: UploadState }) {
	const { t } = useTranslation('common')

	if (uploadState.status === 'uploading') {
		return (
			<>
				<span className="max-w-full truncate text-sm font-medium text-grey-800">
					{t('profile.cv.upload.uploading', { fileName: uploadState.fileName })}
				</span>
				<span className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-grey-200">
					{uploadState.progress === null
						? (
							<span className="block h-full w-2/5 animate-indeterminate rounded-full bg-brand-500 motion-reduce:animate-pulse" />
						)
						: (
							<span
								className="block h-full rounded-full bg-brand-500 transition-[width] duration-200"
								style={{ width: `${uploadState.progress}%` }}
							/>
						)}
				</span>
			</>
		)
	}

	if (uploadState.status === 'done') {
		return (
			<span role="status" className="flex flex-col items-center gap-2">
				<svg
					aria-hidden={true}
					viewBox="0 0 40 40"
					className="size-10 animate-pop-in motion-reduce:animate-none"
				>
					<circle cx="20" cy="20" r="20" className="fill-green-500" />
					<path
						d="M12.5 20.5l5 5 10-11"
						fill="none"
						stroke="white"
						strokeWidth={3}
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeDasharray={24}
						strokeDashoffset={24}
						className="animate-draw-check motion-reduce:animate-none motion-reduce:[stroke-dashoffset:0]"
					/>
				</svg>
				<span className="animate-rise-in-delayed text-sm font-medium text-green-800 motion-reduce:animate-none">
					{t('profile.cv.upload.done')}
				</span>
			</span>
		)
	}

	return (
		<>
			<span className="text-sm font-medium text-brand-600">{t('profile.cv.upload.title')}</span>
			<span className="text-sm text-grey-500">{t('profile.cv.upload.drop_hint')}</span>
			<span className="text-xs text-grey-500">
				{t('profile.cv.upload.constraints', { types: ACCEPTED_TYPES_LABEL, size: MAX_SIZE_IN_MB })}
			</span>
		</>
	)
}
